import type { AccountIdFormattedValue, PublicAccountVM } from '~/composables/useRequest/requests/data-contracts';


export interface FavoriteAccountSource {
  id: number;
  idValue: AccountIdFormattedValue;
  title?: string;
  profile?: {
    alias: string;
    value: {
      presentable?: string;
    };
  }[];
}

function toPublicAccount(account: FavoriteAccountSource): PublicAccountVM {
  const profileTitle = account.profile?.find(item => item.alias === 'title')?.value.presentable;

  return {
    id: account.id,
    idValue: account.idValue,
    title: account.title || profileTitle || '',
  };
}

export const useFavoriteAccountsStore = defineStore('favoriteAccountsStore', () => {
  const { account } = useRequest();
  const favorites = ref<PublicAccountVM[]>([]);
  const pendingIds = ref<number[]>([]);

  const isFavorite = (id: number) => favorites.value.some(item => item.id === id);

  const isPending = (id: number) => pendingIds.value.includes(id);

  const refresh = async () => {
    try {
      const { payload } = await account.favoriteAccountControllerGetFavoriteAccounts();

      favorites.value = payload;

      return true;
    } catch {
      return false;
    }
  };

  const clear = () => {
    favorites.value = [];
    pendingIds.value = [];
  };

  const toggle = async (source: FavoriteAccountSource) => {
    if (isPending(source.id)) {
      return;
    }

    const snapshot = favorites.value.slice();
    const exists = snapshot.some(item => item.id === source.id);

    pendingIds.value = [...pendingIds.value, source.id];

    favorites.value = exists
      ? snapshot.filter(item => item.id !== source.id)
      : [...snapshot, toPublicAccount(source)];

    try {
      if (exists) {
        await account.favoriteAccountControllerRemoveFavorite(source.id);
      } else {
        await account.favoriteAccountControllerAddFavorite(source.id);
      }

      await refresh();
    } catch {
      const restored = await refresh();

      if (!restored) {
        favorites.value = snapshot;
      }
    } finally {
      pendingIds.value = pendingIds.value.filter(id => id !== source.id);
    }
  };

  return {
    favorites,
    pendingIds,
    isFavorite,
    isPending,
    refresh,
    clear,
    toggle,
  };
});
